import React, { useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import axios from "axios";
import Header from "../components/common/Header";
import SideBar from "../components/common/SideBar";
import IntegrationTable from "../components/systemadministration/Integrations/IntegrationTable";
import IntegrationForm from "../components/systemadministration/Integrations/IntegrationForm";
import IntegrationResources from "../components/systemadministration/Integrations/IntegrationResources";
import { GetLtiIntegrationDetail } from "../services/LtiIntegrationService";
import SkeletonTicketList from "../loaders/SkeletonTicketList";
import { usePermissionGuardRedirect } from "../hooks/usePermissionGuard";

/**
 * Entry page for LTI integrations under system administration.
*/
function IntegrationsManagement() {
  const history = useHistory();
  const { tab, id, subTab } = useParams();
  const [integrationDetails, setIntegrationDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  usePermissionGuardRedirect(["integrationsview"]);

  /**
   * Fetches details when user opens an integration.
  */
  const fetchDetails = async (cancelToken) => {
    if (!id) {
      setIntegrationDetails(null);
      return;
    }

    try {
      setLoadingDetails(true);
      const response = await GetLtiIntegrationDetail(id, cancelToken);
      setIntegrationDetails(response?.data?.integration || null);
    } catch (error) {
      setIntegrationDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (tab === "open") {
      fetchDetails(source.token);
    } else {
      setIntegrationDetails(null);
    }

    return () => source.cancel("Component unmounted");
  }, [tab, id]);

  /**
   * Handles successful save flow.
  */
  const handleSaveSuccess = (savedIntegration) => {
    if (tab === "new" && savedIntegration?.id) {
      history.push(`/administration/integrations/open/${savedIntegration.id}/details`);
      return;
    }

    if (tab === "new") {
      history.push("/administration/integrations/table");
      return;
    }

    fetchDetails();
  };

  const detailsTab = subTab || "details";

  return (
    <>
      <Header />
      <div className="content-wrapper d-flex flex-wrap">
        <SideBar />
        <div className="sidebar-right">
          <div className="sub-menu-content-block">
            <div className="sub-menu-right-block">
              <div className="wrap-box-right">
                <div className="my-tickets-heading-blk flex-direction mb-2">
                  <div className="d-flex align-items-center ">
                    <div className="name-w-head d-flex align-items-center ">
                      <h4 className="text-left-align landing-heading heading_color_dynamic">
                        {tab === "table" ? (
                          <>
                            LTI <span>Integrations</span>
                          </>
                        ) : tab === "create" ? (
                          <>
                            Create <span>Integration</span>
                          </>
                        ) : (
                          <>
                            Integration: <span>{integrationDetails?.integration_name || ""}</span>
                          </>
                        )}
                      </h4>
                    </div>
                    <div className="text-md-right action2-blk">
                      <div className="ticket-view-btn d-flex align-items-center">
                        <div className="add-new-ticket-bx">
                          <button
                            onClick={() => history.goBack()}
                            title="Back"
                            className="btn btn-white-bordered"
                          >
                            <i className="fal fa-angle-left"></i>Back
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="tabs-wrap">
                  {tab === "open" && (
                    <ul className="nav nav-pills mb-4" id="tabs" role="tablist">
                      <li className="nav-item">
                        <Link
                          className={`nav-link ${detailsTab === "details" ? "active" : ""}`}
                          id="pills-tab1"
                          to={`/administration/integrations/open/${id}/details`}
                          role="tab"
                        >
                          <i className="fal fa-info-circle"></i>Details
                        </Link>
                      </li>
                      <li className="nav-item">
                        <Link
                          className={`nav-link ${detailsTab === "resources" ? "active" : ""}`}
                          id="pills-tab2"
                          to={`/administration/integrations/open/${id}/resources`}
                          role="tab"
                        >
                          <i className="fal fa-books"></i>Resources
                        </Link>
                      </li>
                    </ul>
                  )}

                  <div className="tab-content" id="pills-tabContent">
                    <div className="tab-pane fade active show" role="tabpanel">
                      {tab === "table" && <IntegrationTable />}
                      {tab === "create" && <IntegrationForm mode="create" onSaved={handleSaveSuccess} />}
                      {tab === "open" && loadingDetails && <SkeletonTicketList />}
                      {tab === "open" && !loadingDetails && detailsTab === "details" && (
                        <IntegrationForm
                          mode="open"
                          integrationId={id}
                          initialData={integrationDetails}
                          onSaved={handleSaveSuccess}
                        />
                      )}
                      {tab === "open" && !loadingDetails && detailsTab === "resources" && (
                        <IntegrationResources integrationId={id} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default IntegrationsManagement;
